import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

function Gallery() {
  const [files, setFiles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [bucketName, setBucketName] = useState(sessionStorage.getItem('bucketName'));

  const fetchFiles = useCallback(async () => {
    try {
      const storageResponse = await axios.get(`${process.env.REACT_APP_API_URL}/storage`);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/files?bucket=${bucketName}&storage=${storageResponse.data.storage}&page=${currentPage}&limit=8`
      );
      setFiles(response.data.files);
      setTotalPages(Math.ceil(response.data.totalFiles / response.data.filesPerPage));
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const isVideo = (mimeType) => {
    return mimeType.startsWith('video/');
  };

  // New function to handle file deletion
  const handleDelete = async (fileId) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/asset/${fileId}`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_API_KEY,
          },
        });
        // After successful deletion, refresh the file list
        fetchFiles();
      } catch (error) {
        console.error('Error deleting file:', error);
        alert('Failed to delete file. Please try again.');
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-3xl font-bold mb-6 text-center text-gray-800 dark:text-white">File Gallery</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {files.map((file) => (
          <div key={file.id} className="bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-300 hover:scale-105">
            <div className="aspect-w-16 aspect-h-9">
              {isVideo(file.mime_type) ? (
                <video controls className="w-full h-full object-cover">
                  <source src={`${process.env.REACT_APP_API_URL}/asset/${file.id}`} type={file.mime_type} />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={`${process.env.REACT_APP_API_URL}/asset/${file.id}`}
                  alt={file.original_name}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="p-4">
              <p className="text-lg font-semibold text-gray-800 mb-2 truncate">{file.original_name}</p>
              <div className="flex justify-between items-center">
                <a
                  href={`${process.env.REACT_APP_API_URL}/asset/${file.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 transition-colors duration-300 dark:text-gray-600"
                >
                  View File
                </a>
                <button
                  onClick={() => handleDelete(file.id)}
                  className="text-red-600 hover:text-red-800 transition-colors duration-300"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8 flex justify-center items-center">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="mx-1 px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>

        {[...Array(totalPages).keys()].slice(Math.max(0, currentPage - 3), Math.min(totalPages, currentPage + 2)).map((page) => (
          <button
            key={page + 1}
            onClick={() => handlePageChange(page + 1)}
            className={`mx-1 px-4 py-2 rounded ${
              currentPage === page + 1
                ? 'bg-blue-600 text-white dark:bg-gray-600 dark:text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-200 dark:text-gray-700'
            } transition-colors duration-300`}
          >
            {page + 1}
          </button>
        ))}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="mx-1 px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default Gallery;